import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import Sentry from 'config/sentry';

import {
  PlaylistTypeEnum,
  SavePlaylistToMyPlaylistsDocument,
  SavePlaylistToMyPlaylistsMutation,
  UserPlaylistsDocument,
  UserPlaylistsOnlyIdDocument,
} from 'graphql/generated/graphql';

import { buildGraphaelQueryOptions } from 'hooks/useGraphaelDefaults';
import { apolloClient } from 'hooks/withGraphQL';

import ReduxState from 'store/ReduxState';

export interface EditorialPlaylistState {
  playlistId?: string;
  postPurchasePath?: string;
}

export const initialEditorialPlaylistState: EditorialPlaylistState = {
  playlistId: undefined,
  postPurchasePath: undefined,
};

export const editorialPlaylistSlice = createSlice({
  name: 'editorialPlaylist',
  initialState: initialEditorialPlaylistState,
  reducers: {
    setEditorialPlaylistId: (
      state,
      action: PayloadAction<Pick<EditorialPlaylistState, 'playlistId'>>,
    ) => {
      state.playlistId = action.payload.playlistId;
    },
    setPostPurchasePath: (state, action: PayloadAction<string>) => {
      state.postPurchasePath = action.payload;
    },
    reset: (state, action: PayloadAction) => {
      state.playlistId = undefined;
    },
  },
});

export const getPostPurchasePath = (state: ReduxState) =>
  state.editorialPlaylist.postPurchasePath;

export const addEditorialPlaylistToPlaylistsIfPresent = createAsyncThunk<
  void,
  void,
  { state: ReduxState }
>(
  'editorialPlaylist/addEditorialPlaylistToPlaylistsIfPresent',
  async (_, { dispatch, getState }) => {
    const {
      auth: { isAuthenticated, headers },
      translations: { locale },
      editorialPlaylist: { playlistId },
    } = getState();

    if (!playlistId) return;

    const queryOptions = buildGraphaelQueryOptions(
      isAuthenticated,
      headers,
      locale,
    );

    const { data, errors } =
      await apolloClient.mutate<SavePlaylistToMyPlaylistsMutation>({
        mutation: SavePlaylistToMyPlaylistsDocument,
        ...queryOptions,
        refetchQueries: [
          {
            query: UserPlaylistsDocument,
            variables: { types: [PlaylistTypeEnum.User] },
            context: queryOptions.context,
          },
          {
            query: UserPlaylistsOnlyIdDocument,
            variables: { types: [PlaylistTypeEnum.User] },
            context: queryOptions.context,
          },
        ],
        variables: {
          input: {
            playlistId,
          },
        },
      });

    if (data?.savePlaylistToMyPlaylists) {
      dispatch(reset());
    } else {
      errors?.forEach((e) => Sentry.captureException(e));
    }
  },
);

export const { setEditorialPlaylistId, setPostPurchasePath, reset } =
  editorialPlaylistSlice.actions;

export default editorialPlaylistSlice.reducer;
